/*
 * Copyright © 2017-2021 Skale Education Inc. - All Rights Reserved
 */

import { store } from "store";
import { ActionCreators } from "actions";

import {
  UpduoApolloClient,
  SkalePrimaryApolloClient,
} from "config/UpduoApolloClient";
import { asyncCallApi } from "config/UpduoService";
import {
  GET_ME_PRIMARY,
  GET_ME,
  GET_PRIMARY_MEMBER_TOKEN,
  GET_PRIMARY_MEMBER_LIST,
} from "graphql/internal/Queries";
import { DEFAULT_STORE, SelfObject, SelfObjectWithoutOrg } from "store/types";
import { GetMeQuery, Scalars } from "graphql/internal/generated";
import { capitalize, keysOf, snakeToCamelCase } from "./Utilities";

import * as UpduoAnalytics from "config/UpduoAnalytics";

export function homeResetState(isAlreadyHome?: boolean) {
  resetFields([
    "IS_POST_EVENT",
    "MATCH_ACK",
    "CHAT_BEGIN_BUTTON_PRESSED",
    "IS_SELF_CHAT_READY",
    "IS_PARTNER_CHAT_READY",
  ]);
  if (!isAlreadyHome) {
    store.dispatch(ActionCreators.setDisplayedSummitView("Summit"));
  }
}

export function startResetState() {
  resetFields([
    "MATCH_OBJECT",
    "SKILL_OBJECT",
    "SKALE_SESSION_OBJECT",
    "CHAT_AGENDA_MODAL_VISIBLE",
  ]);
  const reduxState = store.getState() as any;
  if (reduxState.displayedSummitView !== "Match") {
    store.dispatch(ActionCreators.setPartnerObject({}));
  }
}

export function chatResetState() {
  resetFields(["CHAT_FAILED", "CHAT_CONNECTED", "CHAT_PARTNER_HAS_CANCELLED"]);
}

export async function logoutResetState() {
  asyncCallApi("postLogoutMember");
  asyncCallApi("postLogout");
  // was receiving a query in flight error if stop isn't called, for some reason, there were still queries being made even though user has clicked on log out
  // this should be investigated sometime to clean up the logic before calling logoutResetState
  UpduoApolloClient.stop();
  SkalePrimaryApolloClient.stop();
  await UpduoApolloClient.resetStore();
  await SkalePrimaryApolloClient.resetStore();
  // startObject is not reset, for users' future convenience
  resetStore(["START_OBJECT"]);
}

function resetStore(exclude: Array<keyof typeof DEFAULT_STORE>) {
  resetFields(keysOf(DEFAULT_STORE).filter((key) => !exclude.includes(key)));
}

function resetFields(keys: Array<keyof typeof DEFAULT_STORE>) {
  for (const key of keys) {
    const action = ActionCreators[`set${capitalize(snakeToCamelCase(key))}`];
    store.dispatch(action(DEFAULT_STORE[key]));
  }
}

export function timeResetState() {
  const dayRangeEnd = new Date();
  const dayRangeStart = new Date();
  dayRangeStart.setDate(dayRangeEnd.getDate() - 7); // 1 week ago
  store.dispatch(ActionCreators.setDayRangeStart(dayRangeStart));
  store.dispatch(ActionCreators.setDayRangeEnd(dayRangeEnd));
}

interface InstantiatePrimaryUserArgs {
  failureCallback?: () => void;
  successCallback?: () => void;
  noOrgCallback?: () => void;
  orgCode?: string;
}

export async function instantiatePrimaryUser({
  failureCallback,
  successCallback,
  noOrgCallback,
  orgCode,
}: InstantiatePrimaryUserArgs = {}) {
  try {
    const { data: getPrimaryMemberListData } =
      await SkalePrimaryApolloClient.query({
        query: GET_PRIMARY_MEMBER_LIST,
        fetchPolicy: "network-only",
        variables: {
          primaryMemberUserList: true,
        },
      });
    if (getPrimaryMemberListData.users) {
      const edges = getPrimaryMemberListData.users.edges;
      if (edges.length > 0) {
        const response = edges.map(({ node }) => node);
        // TODO fix redux store state type
        const reduxState = store.getState() as any;
        store.dispatch(ActionCreators.setPrimaryMemberList(response));
        const nonLibraryMembers = response.filter(
          (member) =>
            !process.env.REACT_APP_LIBRARY_ORGANIZATION_IDS?.includes(member.id)
        );
        if (orgCode) {
          // Bring user to a specific org if an org code is provided;
          // otherwise, bring user to 1st org in their list
          const orgMember = response.find(
            (member) =>
              member.organization.orgCode.toLowerCase() ===
              orgCode.toLowerCase()
          );

          if (orgMember) {
            // TODO: figure out why the following lines are bugged, resulting in user forcibly being logged out

            // if (reduxState.authToken) {
            //   await asyncCallApi("postLogoutMember");
            // }
            return await _handlePrimaryMemberLogin(
              orgMember.id,
              successCallback
            );
          } else if (!reduxState.authToken) {
            // When user is not logged into an org membership, go with the first one;
            // otherwise, no need to run _handlePrimaryMemberLogin
            return await _handlePrimaryMemberLogin(
              nonLibraryMembers[0].id,
              successCallback
            );
          }
        } else if (!reduxState.authToken) {
          // When user is not logged into an org membership, go with the first one;
          // otherwise, no need to run _handlePrimaryMemberLogin
          return await _handlePrimaryMemberLogin(
            nonLibraryMembers[0].id,
            successCallback
          );
        }
      } else {
        noOrgCallback && noOrgCallback();
      }
    }
  } catch (error) {
    console.error("ERROR getPrimaryMemberList", error);
    if (failureCallback) {
      failureCallback();
    } else {
      logoutResetState();
    }
  }
}

export const _handlePrimaryMemberLogin = async (
  userId: Scalars["GlobalID"],
  callback?: () => void
) => {
  try {
    // TODO: the below is also bugged, results in error Invariant Violation: Store reset while query was in flight

    // await SkalePrimaryApolloClient.resetStore();
    const { data: getPrimaryMemberTokenData } =
      await SkalePrimaryApolloClient.query({
        query: GET_PRIMARY_MEMBER_TOKEN,
        fetchPolicy: "network-only",
        variables: {
          getPrimaryMemberTokenRequest: {
            userId,
          },
        },
      });
    store.dispatch(
      ActionCreators.setAuthToken(getPrimaryMemberTokenData.user.token)
    );
    UpduoAnalytics.logEvent("user-app-open");
    instantiateMemberUser(callback);
  } catch (error) {
    console.error("getPrimaryMemberToken");
    console.error(error);
  }
};

export async function instantiateMemberUser(callback: () => void = () => {}) {
  const reduxState = store.getState() as any;
  if (!reduxState.authToken) {
    return;
  }
  timeResetState();
  const returnObject = { userHasPhoto: false };
  try {
    const { data: getMeData } = await UpduoApolloClient.query<GetMeQuery>({
      query: GET_ME,
      fetchPolicy: "network-only",
    });
    const user = getMeData.user;
    //TODO all these types should be set to nullable on SelfObject instead of
    //initializing with empty values
    const newSelfObjectBase: SelfObjectWithoutOrg = {
      firstName: user.firstName,
      lastName: user.lastName,
      userId: user.id,
      channelName: user.channelName,
      storeName: user.store?.name || "",
      profileImgLgUrl: user.profileImgLgUrl || "",
      profileImgSmUrl: user.profileImgSmUrl || "",
      districtId: user.district?.id || "",
      score: user.score || 0,
      isAdmin: user.isAdmin,
      isManager: user.isManager,
      isTrainer: user.isTrainer,
      isStaff: user.isStaff,
      isCoach: user.isCoach,
      title: user.title || undefined,
      leaderSummary: user.leaderSummary || {},
      primaryEmail: user.primaryEmail || "",
    };
    console.log(user, "user");
    const newSelfObject: SelfObject | SelfObjectWithoutOrg = user.organization
      ? {
          ...newSelfObjectBase,
          organizationName: user.organization.name,
          organizationId: user.organization.id,
          organizationShowDistrict: user.organization.showDistrict,
          organizationShowTranscription: user.organization.showTranscription,
          organizationIsOpen: user.organization.isOpen,
          organizationAllowQueueReentry: user.organization.isOpen,
          organizationHasTimeClockAlert: user.organization.hasTimeClockAlert,
          organizationArchiveRatio: user.organization.archiveRatio,
          organizationStartButtonText: user.organization.startButtonText,
          organizationLaunchDate: user.organization.launchDate,
          organizationCode: user.organization.orgCode,
          organizationType: user.organization.orgType,
          organizationAuth0ConnectionName:
            user.organization.auth0ConnectionName || undefined,
          organizationDoAgoraForceTcp: user.organization.doAgoraForceTcp,
          organizationActiveQuota: user.organization.activeQuota || undefined,
          organizationChannelName: user.organization.channelName,
          organizationManagerDashboardEnabled:
            user.organization.managerDashboardEnabled,
          organizationCachedFieldsLastUpdated:
            user.organization.cachedFieldsLastUpdated,
          organizationUserCount: user.organization.userCount,
          organizationUserproxyCount: user.organization.userproxyCount,
        }
      : newSelfObjectBase;

    // Hack for "library" organizations:
    // Only Content page for non-Maintainance users, plus Members page and editing only for Maintenance users:
    const libraryOrganizationIds =
      process.env.REACT_APP_LIBRARY_ORGANIZATION_IDS?.split(",") || [];
    const isLibraryOrganization = libraryOrganizationIds.includes(
      user?.organization?.id || ""
    );
    if (isLibraryOrganization) {
      window.location.href = "#/content";
    }

    // Check if user has profile pic.
    const imageExists = async (url: string) => {
      const img = new Image();

      return new Promise<boolean>((resolve) => {
        img.onload = () => {
          resolve(true);
        };
        img.onerror = () => {
          resolve(false);
        };
        img.src = url;
      });
    };
    returnObject.userHasPhoto = await imageExists(
      newSelfObject.profileImgSmUrl
    );
    //TODO refactor the SelfObject redux type to allow for an empty value
    //@ts-expect-error
    store.dispatch(ActionCreators.setSelfObject(newSelfObject));
    callback();
  } catch (error) {
    console.error("ERROR getMe");
    console.error(error);
    UpduoAnalytics.logEvent(
      "app-getme-error",
      error ? JSON.stringify(error) : undefined
    );
    logoutResetState();
  }

  try {
    const { data: getMePrimaryData } = await SkalePrimaryApolloClient.query({
      query: GET_ME_PRIMARY,
      fetchPolicy: "network-only",
    });

    let userPropertiesObject: Object = {
      email: reduxState.startObject.email,
    };

    UpduoAnalytics.setUserId(String(getMePrimaryData.user.id));
    userPropertiesObject = {
      user_id: String(getMePrimaryData.user.id),
      first_name: getMePrimaryData.user.firstName,
      last_name: getMePrimaryData.user.lastName,
      email: getMePrimaryData.user.email,
    };

    // Submit relevant user info for analytics
    UpduoAnalytics.setUserProperties(userPropertiesObject);
  } catch (error) {
    console.error("ERROR getMePrimary");
    console.error(error);
    UpduoAnalytics.logEvent(
      "app-getmeprimary-error",
      error ? JSON.stringify(error) : undefined
    );
  }
  return returnObject;
}
